import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useLoader } from "../../../context/LoaderContext";
import { ESourceAccessGate, useESourceAccess } from "./esourceAccess";
import {
  amendField,
  createFillLink,
  fetchInstanceById,
  lockInstance,
  parseResponseJson,
  parseSchemaJson,
  revokeFillLink,
  saveDraftResponse,
  signInstance,
  submitForReview,
} from "./esourceService";
import {
  applyFieldDefaults,
  collectRequiredMissing,
  collectFieldValidationIssues,
  evaluateLogicVisibility,
  FILL_ACCESS_ANYWHERE,
  isLayoutField,
} from "./esourceFieldTypes";
import { ESourceFormFields } from "./esourceFormRender";
import "./ESourcePages.scss";

const ESourceInstanceRuntime = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const access = useESourceAccess();
  const { showLoader, hideLoader } = useLoader();
  const [instance, setInstance] = useState(null);
  const [values, setValues] = useState({});
  const [message, setMessage] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [fillUrl, setFillUrl] = useState("");
  const [amend, setAmend] = useState({
    fieldId: "",
    errorCode: "",
    reason: "",
    newValue: "",
  });

  const load = useCallback(async () => {
    showLoader();
    try {
      const res = await fetchInstanceById(id);
      if (!res.success || !res.instance) {
        setMessage(res.message || "Form record not found.");
        setInstance(null);
        return;
      }
      setInstance(res.instance);
      const schema = parseSchemaJson(res.instance.SchemaJson);
      setValues(
        applyFieldDefaults(
          schema.fields,
          parseResponseJson(res.instance.ResponseJson),
        ),
      );
    } finally {
      hideLoader();
    }
  }, [id, showLoader, hideLoader]);

  useEffect(() => {
    if (!access.loading) load();
  }, [access.loading, load]);

  const fields = useMemo(
    () => parseSchemaJson(instance?.SchemaJson).fields,
    [instance],
  );
  const schemaExtras = useMemo(() => {
    const schema = parseSchemaJson(instance?.SchemaJson);
    return {
      logic: schema.logic || [],
      layout: schema.settings?.layout || "two-column",
    };
  }, [instance]);
  const formIntro = useMemo(() => {
    const schema = parseSchemaJson(instance?.SchemaJson);
    return {
      header: schema.formHeader || "",
      description: schema.formDescription || "",
    };
  }, [instance]);
  const fillAccess = useMemo(
    () => parseSchemaJson(instance?.SchemaJson).fillAccess,
    [instance],
  );
  const allowExternalFill = fillAccess === FILL_ACCESS_ANYWHERE;

  const status = String(instance?.Status || "");
  const isEditable = status === "Draft" || status === "InReview";
  const isSignedOrLocked = status === "Signed" || status === "Locked";

  const setFieldValue = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const responseJson = JSON.stringify(values);

  const onSaveDraft = async () => {
    showLoader();
    try {
      const res = await saveDraftResponse({
        FormInstanceId: id,
        ResponseJson: responseJson,
        Title: instance?.Title,
      });
      setMessage(res.message || "");
      if (res.success) await load();
    } finally {
      hideLoader();
    }
  };

  const onSubmitReview = async () => {
    const { visible } = evaluateLogicVisibility(
      fields,
      values,
      schemaExtras.logic,
    );
    const missing = collectRequiredMissing(fields, values, visible);
    if (missing.length) {
      setMessage(
        `Please complete required fields: ${missing.map((f) => f.label).join(", ")}`,
      );
      return;
    }
    const issues = collectFieldValidationIssues(fields, values, visible);
    if (issues.length) {
      setMessage(issues.map((i) => i.message).join(" "));
      return;
    }
    showLoader();
    try {
      const res = await submitForReview({
        FormInstanceId: id,
        ResponseJson: responseJson,
        NotifyEmail: notifyEmail || null,
        Title: instance?.Title,
      });
      setMessage(res.message || "");
      if (res.success) await load();
    } finally {
      hideLoader();
    }
  };

  const onSign = async () => {
    showLoader();
    try {
      const res = await signInstance({
        FormInstanceId: id,
        ResponseJson: responseJson,
        NotifyEmail: notifyEmail || null,
        Title: instance?.Title,
      });
      setMessage(res.message || "");
      if (res.success) await load();
    } finally {
      hideLoader();
    }
  };

  const onLock = async () => {
    showLoader();
    try {
      const res = await lockInstance(id);
      setMessage(res.message || "");
      if (res.success) await load();
    } finally {
      hideLoader();
    }
  };

  const onCreateFillLink = async () => {
    showLoader();
    try {
      const res = await createFillLink({
        FormInstanceId: id,
        RecipientEmail: notifyEmail || null,
        ExpiryDays: 14,
      });
      setMessage(res.message || "");
      if (res.success && res.fillUrl) {
        setFillUrl(res.fillUrl);
        try {
          await navigator.clipboard.writeText(res.fillUrl);
          setMessage(
            (res.message || "External link created.") +
              " Link copied to clipboard.",
          );
        } catch {
          /* clipboard may be blocked */
        }
      }
    } finally {
      hideLoader();
    }
  };

  const onRevokeFillLink = async () => {
    showLoader();
    try {
      const res = await revokeFillLink({ FormInstanceId: id });
      setMessage(res.message || "");
      if (res.success) setFillUrl("");
    } finally {
      hideLoader();
    }
  };

  const onAmend = async () => {
    if (!amend.fieldId || !amend.errorCode.trim() || !amend.reason.trim()) {
      setMessage("Field, error code and reason are required for amend.");
      return;
    }
    const oldValue =
      values[amend.fieldId] != null ? String(values[amend.fieldId]) : "";
    const nextValues = { ...values, [amend.fieldId]: amend.newValue };
    showLoader();
    try {
      const res = await amendField({
        FormInstanceId: id,
        FieldId: amend.fieldId,
        OldValue: oldValue,
        NewValue: amend.newValue,
        ErrorCode: amend.errorCode.trim(),
        Reason: amend.reason.trim(),
        ResponseJson: JSON.stringify(nextValues),
      });
      setMessage(res.message || "");
      if (res.success) {
        setAmend({ fieldId: "", errorCode: "", reason: "", newValue: "" });
        await load();
      }
    } finally {
      hideLoader();
    }
  };

  const amendableFields = useMemo(
    () => fields.filter((f) => !isLayoutField(f.type)),
    [fields],
  );

  if (!instance && !message && !access.loading) return null;

  return (
    <ESourceAccessGate
      loading={access.loading}
      allow={access.canView || access.canFill}
    >
    <div className="esource-page">
      <PageBreadcrumb
        items={[
          { label: "e-Source", path: "/esource/templates" },
          { label: "Form records", path: "/esource/instances" },
          { label: instance?.Title || "Form record" },
        ]}
      />
      <div className="esource-page__header">
        <div>
          <h1 className="esource-page__title">
            {instance?.Title || instance?.TemplateName || "Form record"}
          </h1>
          <p className="esource-page__subtitle">
            {instance?.TemplateCode} · v{instance?.VersionNo} ·{" "}
            <span className={`esource-status esource-status--${status.toLowerCase()}`}>
              {status || "—"}
            </span>
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate("/esource/instances")}
        >
          Back to form records
        </button>
      </div>

      {message ? <div className="esource-alert">{message}</div> : null}

      {isEditable &&
      allowExternalFill &&
      (access.canFill || access.canDesign) ? (
        <div className="esource-card">
          <h2 className="esource-page__section-title">External fill link</h2>
          <p className="esource-page__subtitle mb-2">
            Create a secure link so someone can complete this form without
            logging in. Copy and share the link as needed.
          </p>
          {fillUrl ? (
            <div className="esource-fill-link-box mb-2">
              <input className="form-control" readOnly value={fillUrl} />
            </div>
          ) : null}
          <div className="esource-actions">
            <button
              type="button"
              className="btn btn-esource"
              onClick={onCreateFillLink}
            >
              Create / refresh link
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onRevokeFillLink}
            >
              Revoke active link
            </button>
          </div>
        </div>
      ) : null}

      <div className="esource-card">
        {formIntro.header || formIntro.description ? (
          <div className="esource-form-intro-block">
            {formIntro.header ? (
              <h2 className="esource-form-intro-block__header">
                {formIntro.header}
              </h2>
            ) : null}
            {formIntro.description ? (
              <p className="esource-form-intro-block__description">
                {formIntro.description}
              </p>
            ) : null}
          </div>
        ) : null}
        <ESourceFormFields
          fields={fields}
          values={values}
          disabled={!isEditable}
          onChange={setFieldValue}
          emptyText="This template has no fields."
          logic={schemaExtras.logic}
          layout={schemaExtras.layout}
        />
      </div>

      {isEditable ? (
        <div className="esource-card">
          <label className="form-label">Notify email (optional)</label>
          <input
            className="form-control mb-3"
            type="email"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="Reviewer or notify address"
          />
          <div className="esource-actions">
            {access.canFill ? (
              <button type="button" className="btn btn-outline-secondary" onClick={onSaveDraft}>
                Save draft
              </button>
            ) : null}
            {access.canFill || access.canReview ? (
              <button type="button" className="btn btn-outline-primary" onClick={onSubmitReview}>
                Submit for review
              </button>
            ) : null}
            {access.canSign ? (
              <button type="button" className="btn btn-esource" onClick={onSign}>
                Sign
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {status === "Signed" && access.canSign ? (
        <div className="esource-card">
          <button type="button" className="btn btn-esource" onClick={onLock}>
            Lock form
          </button>
        </div>
      ) : null}

      {isSignedOrLocked && access.canAmend ? (
        <div className="esource-card">
          <h2 className="esource-page__section-title">GCP amendment</h2>
          <p className="esource-page__subtitle">
            Strike-through style correction: error code, reason, and re-record values with audit.
          </p>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Field</label>
              <select
                className="form-select"
                value={amend.fieldId}
                onChange={(e) =>
                  setAmend((a) => ({
                    ...a,
                    fieldId: e.target.value,
                    newValue:
                      values[e.target.value] != null
                        ? String(values[e.target.value])
                        : "",
                  }))
                }
              >
                <option value="">Select field…</option>
                {amendableFields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Error code</label>
              <input
                className="form-control"
                value={amend.errorCode}
                onChange={(e) =>
                  setAmend((a) => ({ ...a, errorCode: e.target.value }))
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">New value</label>
              <input
                className="form-control"
                value={amend.newValue}
                onChange={(e) =>
                  setAmend((a) => ({ ...a, newValue: e.target.value }))
                }
              />
            </div>
            <div className="col-12">
              <label className="form-label">Reason</label>
              <textarea
                className="form-control"
                rows={2}
                value={amend.reason}
                onChange={(e) =>
                  setAmend((a) => ({ ...a, reason: e.target.value }))
                }
              />
            </div>
          </div>
          <button type="button" className="btn btn-esource mt-3" onClick={onAmend}>
            Record amendment
          </button>
        </div>
      ) : null}
    </div>
    </ESourceAccessGate>
  );
};

export default ESourceInstanceRuntime;
